import React, { useState } from "react";
import api from "../../lib/api";
import { Compass, Eye, Lightning, Warning, CheckCircle, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * GEO-2 / GEO-2B: Admin-only "Resolve unmapped locations" workflow.
 *
 * Preview (dry_run=true) → Resolve Locations (dry_run=false).
 * After Preview only Preview cards are shown.
 * After Resolve, Preview cards are hidden and only Resolve cards are shown
 * until the next Preview.
 *
 * Limit: 200 unique pairs per run (matches backend hard ceiling).
 */
const RUN_LIMIT = 200;

export default function GeocodeResolverPanel({
  compact = false,
  onResolved,
  contextLabel,
}) {
  // Phase state: "idle" | "preview" | "resolve"
  const [phase, setPhase] = useState("idle");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [lastResolve, setLastResolve] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/locations/geocode-unmapped", {
        limit: RUN_LIMIT,
        dry_run: true,
      });
      setPreview(data);
      setLastResolve(null);
      setPhase("preview");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const runResolve = async () => {
    setResolving(true);
    try {
      const { data } = await api.post("/locations/geocode-unmapped", {
        limit: RUN_LIMIT,
        dry_run: false,
      });
      setLastResolve(data);
      setPreview(null); // hide Preview cards/details after Resolve (per GEO-2B)
      setPhase("resolve");
      toast.success(
        `Geocoded ${data.geocoded} · cached ${data.already_cached} · review ${data.needs_review} · failed ${data.failed}`
      );
      if (typeof onResolved === "function") onResolved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Geocoding failed");
    } finally {
      setResolving(false);
    }
  };

  const candidates = preview?.details?.filter((d) => d.status === "would_geocode") || [];
  const needsReview = preview?.details?.filter((d) => d.status === "needs_review") || [];
  const skipped = preview?.details?.filter(
    (d) => d.status === "skipped" || d.status === "failed"
  ) || [];

  const moreRemain =
    (phase === "preview" && candidates.length > RUN_LIMIT) ||
    (phase === "resolve" && lastResolve && lastResolve.checked >= RUN_LIMIT);

  return (
    <div
      data-testid="geocode-resolver-panel"
      className={`bg-white border border-gray-200 rounded-lg ${compact ? "p-4" : "p-5"} space-y-4`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">
            <Compass size={14} weight="duotone" /> Location resolution
          </div>
          <h3 className="font-display text-lg font-bold tracking-tight mt-1">
            Resolve unmapped locations
          </h3>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            {contextLabel ||
              "Preview unmapped city/country pairs, then geocode valid locations using the backend cache."}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5">up to {RUN_LIMIT} per run</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={runPreview}
            disabled={previewing || resolving}
            data-testid="geocode-preview-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          >
            <Eye size={14} weight="bold" />
            {previewing ? "Previewing…" : "Preview locations"}
          </button>
          <button
            type="button"
            onClick={runResolve}
            disabled={phase !== "preview" || resolving || candidates.length === 0}
            data-testid="geocode-resolve-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-[#E1A104] text-[#1A3A21] hover:bg-[#C98F03] disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              phase !== "preview"
                ? "Run Preview first"
                : candidates.length === 0
                ? "No candidates to resolve"
                : ""
            }
          >
            <Lightning size={14} weight="fill" />
            {resolving ? "Resolving…" : "Resolve Locations"}
          </button>
        </div>
      </div>

      {/* Preview cards — only while phase === 'preview' */}
      {phase === "preview" && preview && (
        <>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            Preview result
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="geocode-preview-summary">
            <SummaryPill label="Candidates" value={candidates.length} tone="green" />
            <SummaryPill label="Needs review" value={needsReview.length} tone="yellow" />
            <SummaryPill label="Skipped" value={skipped.length} tone="red" />
            <SummaryPill label="Checked" value={preview.checked} tone="gray" />
          </div>
        </>
      )}

      {/* Resolve cards — only while phase === 'resolve' */}
      {phase === "resolve" && lastResolve && (
        <>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            Resolve result
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="geocode-resolve-summary">
            <SummaryPill label="Geocoded" value={lastResolve.geocoded} tone="green" />
            <SummaryPill label="Already cached" value={lastResolve.already_cached} tone="blue" />
            <SummaryPill label="Needs review" value={lastResolve.needs_review} tone="yellow" />
            <SummaryPill label="Failed" value={lastResolve.failed} tone="red" />
            <SummaryPill label="Skipped" value={lastResolve.skipped} tone="gray" />
          </div>
        </>
      )}

      {moreRemain && (
        <div
          data-testid="geocode-more-batches"
          className="flex items-center gap-2 text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-md p-2.5"
        >
          <Info weight="bold" /> More locations remain. Run another batch.
        </div>
      )}

      {/* Preview details (only while in preview phase) */}
      {phase === "preview" && preview && (
        <details className="text-xs" data-testid="geocode-preview-details">
          <summary className="cursor-pointer font-semibold text-gray-700">
            Preview details ({preview.details?.length || 0} unique pair
            {preview.details?.length === 1 ? "" : "s"})
          </summary>
          <div className="mt-3 space-y-3">
            {candidates.length > 0 && (
              <DetailGroup
                title="Will be geocoded"
                tone="green"
                icon={<CheckCircle weight="bold" />}
                items={candidates.slice(0, RUN_LIMIT)}
                testid="geocode-group-candidates"
              />
            )}
            {needsReview.length > 0 && (
              <DetailGroup
                title="Needs review"
                tone="yellow"
                icon={<Warning weight="bold" />}
                items={needsReview}
                testid="geocode-group-review"
                helperText="These locations are ambiguous, dirty, region-like, placeholders, or have country mismatches. They were not geocoded automatically."
              />
            )}
            {skipped.length > 0 && (
              <DetailGroup
                title="Skipped / failed"
                tone="red"
                icon={<Warning weight="bold" />}
                items={skipped}
                testid="geocode-group-skipped"
              />
            )}
          </div>
        </details>
      )}

      {/* Resolve details (only while in resolve phase) */}
      {phase === "resolve" && lastResolve && (
        <details className="text-xs" data-testid="geocode-resolve-details">
          <summary className="cursor-pointer font-semibold text-gray-700">
            Resolve details ({lastResolve.details?.length || 0} unique pair
            {lastResolve.details?.length === 1 ? "" : "s"})
          </summary>
          <div className="mt-3 space-y-3">
            <DetailGroup
              title="Geocoded"
              tone="green"
              icon={<CheckCircle weight="bold" />}
              items={lastResolve.details?.filter((d) => d.status === "geocoded") || []}
              testid="geocode-resolve-group-ok"
            />
            <DetailGroup
              title="Needs review"
              tone="yellow"
              icon={<Warning weight="bold" />}
              items={lastResolve.details?.filter((d) => d.status === "needs_review") || []}
              testid="geocode-resolve-group-review"
              helperText="These locations are ambiguous, dirty, region-like, placeholders, or have country mismatches. They were not geocoded automatically."
            />
            <DetailGroup
              title="Skipped / failed"
              tone="red"
              icon={<Warning weight="bold" />}
              items={lastResolve.details?.filter(
                (d) => d.status === "skipped" || d.status === "failed"
              ) || []}
              testid="geocode-resolve-group-skipped"
            />
          </div>
        </details>
      )}
    </div>
  );
}

function SummaryPill({ label, value, tone }) {
  const tones = {
    green: "border-green-200 bg-green-50 text-green-800",
    yellow: "border-yellow-200 bg-yellow-50 text-yellow-800",
    red: "border-red-200 bg-red-50 text-red-700",
    gray: "border-gray-200 bg-gray-50 text-gray-700",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
  }[tone];
  return (
    <div className={`border rounded-md px-3 py-2 ${tones}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
      <div className="font-display text-xl font-bold leading-none mt-1">{value}</div>
    </div>
  );
}

function DetailGroup({ title, tone, icon, items, testid, helperText }) {
  if (!items || items.length === 0) return null;
  const headerTone = {
    green: "text-green-800",
    yellow: "text-yellow-800",
    red: "text-red-700",
  }[tone];
  return (
    <div data-testid={testid}>
      <div className={`flex items-center gap-1.5 font-semibold mb-1 ${headerTone}`}>
        {icon} {title} ({items.length})
      </div>
      {helperText && <p className="text-[11px] text-gray-500 mb-1.5">{helperText}</p>}
      <div className="border border-gray-200 rounded-md overflow-auto max-h-56">
        <table className="w-full text-[11px]">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left py-1.5 px-2 font-semibold text-gray-500">City</th>
              <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Country</th>
              <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Resolved</th>
              <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Reason</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d, i) => (
              <tr key={d.cache_key || i} className="border-t border-gray-100">
                <td className="py-1.5 px-2 text-gray-800">{d.city || "—"}</td>
                <td className="py-1.5 px-2 text-gray-600">{d.country || "—"}</td>
                <td className="py-1.5 px-2 text-gray-600">
                  {d.candidate && d.candidate !== d.city ? d.candidate : "—"}
                  {d.canon_country && d.canon_country !== d.country
                    ? `, ${d.canon_country}`
                    : ""}
                </td>
                <td className="py-1.5 px-2 text-gray-500" title={d.reason}>
                  {d.reason ||
                    (d.status === "would_geocode" || d.status === "geocoded" ? "—" : "")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
